// Lightweight consent-gated visitor tracking for noq5.ai.
// - Sets a first-party visitor_id + session_id cookie only after consent.
// - Deterministic identification via ?v=<contact_id> or identify(email).
// - Attaches signed-in NoQ5 user_id when present (attribution to a real user).
// - All writes go through the visitor-ingest edge function (never direct DB).

import { supabase } from "@/integrations/supabase/client";

const VID_COOKIE = "noq5_vid";
const CONSENT_COOKIE = "noq5_consent";
const SESSION_KEY = "noq5_sid";
const FIRST_TOUCH_KEY = "noq5_ft";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const INGEST_URL = `${SUPABASE_URL}/functions/v1/visitor-ingest`;

type Utm = { source?: string; medium?: string; campaign?: string; content?: string; term?: string };

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // Fallback (should not hit in modern browsers)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, days: number) {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

export function hasConsent(): boolean {
  return readCookie(CONSENT_COOKIE) === "accepted";
}

export function grantConsent() {
  writeCookie(CONSENT_COOKIE, "accepted", 365);
  // Ensure visitor_id exists so the very next pageview is tracked
  ensureVisitorId();
  window.dispatchEvent(new CustomEvent("noq5:consent", { detail: { accepted: true } }));
}

export function denyConsent() {
  writeCookie(CONSENT_COOKIE, "denied", 365);
  window.dispatchEvent(new CustomEvent("noq5:consent", { detail: { accepted: false } }));
}

export function consentStatus(): "accepted" | "denied" | "unknown" {
  const v = readCookie(CONSENT_COOKIE);
  if (v === "accepted") return "accepted";
  if (v === "denied") return "denied";
  return "unknown";
}

function ensureVisitorId(): string {
  let id = readCookie(VID_COOKIE);
  if (!id) {
    id = uuid();
    writeCookie(VID_COOKIE, id, 365);
  }
  return id;
}

function ensureSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuid();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return uuid();
  }
}

function parseUtm(search: string): Utm {
  const p = new URLSearchParams(search);
  const pick = (k: string) => p.get(k) ?? undefined;
  return {
    source: pick("utm_source"),
    medium: pick("utm_medium"),
    campaign: pick("utm_campaign"),
    content: pick("utm_content"),
    term: pick("utm_term"),
  };
}

function readFirstTouch(): { landing?: string; referrer?: string; utm?: Utm } {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  const ft = {
    landing: window.location.pathname + window.location.search,
    referrer: document.referrer || undefined,
    utm: parseUtm(window.location.search),
  };
  try { localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(ft)); } catch { /* ignore */ }
  return ft;
}

async function post(payload: Record<string, unknown>) {
  if (!hasConsent()) return;
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return;
  try {
    await fetch(INGEST_URL, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(payload),
    });
  } catch {
    // Never let tracking break the app.
  }
}

function stripIdentityParam() {
  const url = new URL(window.location.href);
  if (url.searchParams.has("v")) {
    url.searchParams.delete("v");
    window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);
  }
}

async function currentUserId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

function basePayload() {
  const ft = readFirstTouch();
  return {
    visitor_id: ensureVisitorId(),
    first_landing_page: ft.landing,
    first_referrer: ft.referrer,
    first_utm: ft.utm ?? {},
    consent_version: "v1",
  };
}

export async function trackPageview(path: string) {
  if (!hasConsent()) return;
  const url = new URL(window.location.href);
  await post({
    ...basePayload(),
    signed_in_user_id: await currentUserId(),
    action: "pageview",
    session_id: ensureSessionId(),
    path,
    referrer: document.referrer || undefined,
    utm: parseUtm(url.search),
  });
}

export async function identify(email: string, extras?: { name?: string; company?: string; contactId?: string; via?: "link" | "form" }) {
  if (!hasConsent()) return;
  if (!email) return;
  await post({
    ...basePayload(),
    signed_in_user_id: await currentUserId(),
    action: "identify",
    email,
    name: extras?.name,
    company: extras?.company,
    contact_id: extras?.contactId,
    identified_via: extras?.via ?? "form",
    path: window.location.pathname,
  });
}

export async function trackEvent(name: string, payload: Record<string, unknown> = {}) {
  if (!hasConsent()) return;
  await post({
    ...basePayload(),
    action: "event",
    event_name: name,
    path: window.location.pathname,
    payload,
  });
}

// Process ?v=<contact_id> once per visit
export async function processIdentityParam() {
  if (typeof window === "undefined") return;
  if (!hasConsent()) return;
  const url = new URL(window.location.href);
  const v = url.searchParams.get("v");
  if (!v) return;
  await post({
    ...basePayload(),
    action: "identify",
    contact_id: v,
    identified_via: "link",
    path: window.location.pathname,
  });
  stripIdentityParam();
}
